import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// Universal international phone format (E.164-like):
// "+" followed by 7-15 digits. Spaces are allowed for readability.
const rePhone = /^\+[1-9](?:[\s]?\d){6,14}$/;

function maskInternationalPhone(value) {
  // Keep digits only, then always display the number with a leading "+".
  const digits = value.replace(/\D/g, "").slice(0, 15);

  if (!digits) {
    return value.includes("+") ? "+" : "";
  }

  // Group digits for readability without assuming any country's
  // national numbering format. Example: +380 67 123 45 67.
  const groups = [];
  let index = 0;

  // First group is the beginning of the international number.
  // We intentionally don't try to guess the exact country-code length.
  groups.push(digits.slice(0, Math.min(3, digits.length)));
  index = groups[0].length;

  while (index < digits.length) {
    groups.push(digits.slice(index, index + 3));
    index += 3;
  }

  return `+${groups.join(" ")}`;
}

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "",  message: "" });
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState({ loading: false, ok: null, msg: "" });
  const navigate = useNavigate();

  const errors = {
    name: !form.name ? "Enter a name" : "",
    email: !form.email ? "Enter your email address." : !/^\S+@\S+\.\S+$/.test(form.email) ? "Incorrect email" : "",
    phone: !form.phone ? "Enter your phone number" : !rePhone.test(form.phone) ? "International format: + country code and phone number" : "",
    message: !form.message ? "Enter message" : ""
  };

  function handleChange(e) {
    const { name, value } = e.target;
    if (name === "phone") {
      setForm((f) => ({ ...f, phone: maskInternationalPhone(value) }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  }
  function onBlur(e) {
    setTouched((t) => ({ ...t, [e.target.name]: true }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setTouched({ name: true, phone: true, email: true, message: true});
    if (errors.name || errors.phone || errors.email || errors.message) return;

    try {
          setStatus({ loading: true, ok: null, msg: "" });

          const res = await fetch("/pp-api/forms/contact", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...form}),  //, recaptchaToken
          });

          if (!res.ok) {
            const t = await res.text();
            throw new Error(t || "Server error");
          }

          //setStatus({ loading: false, ok: true, msg: "The request to call the technician has been sent!" });
          setForm({ name: "", phone: "", email: "", message: "" });
          setTouched({});
          setStatus({ loading: false, ok: true, msg: "" });
          navigate("/thank-you");

        } catch (err) {
          setStatus({ loading: false, ok: false, msg: "Failed to send. Please try again later." });
        }

  }

  return (
    <section id="contact" className="block block-dark block-contact">
      <div className="container text-center">
        <h2 className="fw-bold">Any remaining questions? Contact us!</h2>
        <div className="block-description">
          Fill out the form and we will contact you!
        </div>
        <div className="row">
          <div className="col-md-12">
            <form className="form-contact text-start needs-validation" onSubmit={handleSubmit} noValidate>
              <div className="form-body">
                <div className="row g-3">
                  <div className="col-md-12">
                    <input
                      className={`form-control ${touched.name && errors.name ? "is-invalid" : touched.name ? "is-valid" : ""}`}
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      onBlur={onBlur}
                      required
                      placeholder="Name"
                    />
                    <div className="invalid-feedback">{errors.name}</div>
                  </div>

                  <div className="col-md-12">
                    <input
                      className={`form-control ${touched.email && errors.email ? "is-invalid" : touched.email ? "is-valid" : ""}`}
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      onBlur={onBlur}
                      required
                      placeholder="E-mail"
                    />
                    <div className="invalid-feedback">{errors.email}</div>
                  </div>

                  <div className="col-md-12">
                    <input
                      className={`form-control ${touched.phone && errors.phone ? "is-invalid" : touched.phone ? "is-valid" : ""}`}
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      onBlur={onBlur}
                      required
                      inputMode="tel"
                      pattern={rePhone.source}
                      placeholder="+1 555 123 4567"
                    />
                    <div className="invalid-feedback">{errors.phone}</div>
                  </div>

                  <div className="col-md-12">
                  <textarea
                    className={`form-control ${touched.message && errors.message ? "is-invalid" : touched.message ? "is-valid" : ""}`}
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    onBlur={onBlur}
                    required
                    placeholder="Message"
                    ></textarea>
                     <div className="invalid-feedback">{errors.message}</div>
                  </div>

                </div>

                <button className="btn btn-primary mt-4 w-100 " disabled={status.loading}>
                      {status.loading ? "Sending..." : "Contact us"}
                </button>

                {status.msg && (
                    <div className={`alert mt-3 ${status.ok ? "alert-success" : "alert-danger"}`} role="alert" aria-live="polite">
                        {status.msg}
                    </div>
                )}

              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
